/**
 * Feature E05-F08 — prompt-run journal & test replay contracts.
 * Spec: docs/superpowers/specs/2026-09-07-ai-prompt-test-replay-design.md#api-только-для-роли-admin
 *
 * This file carries the whole journal API: the READ endpoints (`GET …/runs`,
 * `GET …/runs/:id` — T04) and the test call (`POST …/test` — T05), so the shapes
 * the admin window is built on are described in one place.
 *
 * WHY A TEST ENDPOINT AT ALL: an admin tuning a prompt has to see what the
 * template actually sends and what the model actually answers, on the SAME data
 * a real run used. Everything here therefore describes a REPLAY: recorded
 * `vars`/`defaults` of one journal row + a draft template = one single model
 * call. The draft is NOT saved (saving is a separate PATCH) — otherwise every
 * tuning iteration would go live for all users before it is any good.
 *
 * Invariants the shapes encode:
 *   - `runId` / `replayOfId` are `ai_call_log.id` — BigInt server-side, carried
 *     as a decimal STRING on the wire (JSON has no 64-bit integers);
 *   - `parseOk` is TRI-STATE: `true`/`false` for prompts whose `output_format`
 *     promises JSON, and `null` when parsing does not apply (a plain-text
 *     prompt such as `search_salary_rate`) — the window must not paint a
 *     plain-text answer as a parse failure;
 *   - `error` inside a 200 result: a failed model call is a legitimate OUTCOME
 *     of a test (429 / 529 / timeout), shown next to the prompt that was sent —
 *     not an HTTP failure that would throw away `renderedPrompt`/`missingVars`;
 *   - `runId` is nullable: the journal is an observer and never breaks the call
 *     it records, so a failed log write still returns the (paid-for) answer.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Placeholder helpers (shared by the server renderer and the test window)
// ---------------------------------------------------------------------------
/**
 * `{{ name }}` — the placeholder shape `substituteVariables()`
 * (`packages/api/src/services/llm-prompt-loader.ts`) replaces. Whitespace inside
 * the braces is tolerated here because the seeded templates contain both forms.
 */
const PROMPT_PLACEHOLDER_RE = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;
/** Every `{{placeholder}}` of a template, de-duplicated, in first-use order. */
export function extractPromptPlaceholders(template) {
    const seen = new Set();
    const out = [];
    for (const m of String(template ?? '').matchAll(PROMPT_PLACEHOLDER_RE)) {
        const name = m[1];
        if (name === undefined || seen.has(name))
            continue;
        seen.add(name);
        out.push(name);
    }
    return out;
}
/**
 * Placeholders of the template that are STILL IN the rendered prompt — i.e. the
 * ones the selected run could not fill. This is the spec's "variable drift"
 * warning, and it is load-bearing: `substituteVariables()` leaves an unfilled
 * placeholder in the text VERBATIM, so the model literally receives
 * `{{roleName}}` and answers around it. Unnamed, that reads as the prompt
 * misbehaving.
 *
 * IT IS COMPUTED FROM THE RENDERED STRING, not from `vars`/`defaults` — because
 * "has a value somewhere" and "was actually substituted" are NOT the same thing
 * in this codebase: `substituteVariables()` iterates over the KEYS OF `vars`, so
 * a per-placeholder DEFAULT only applies to a key that exists in `vars` with an
 * empty value. A placeholder that has a default but no `vars` entry goes out
 * with the braces intact. Deriving the warning from the string that will be sent
 * cannot drift from the substitution rules, whatever they are.
 */
export function findUnfilledPromptVars(template, renderedPrompt) {
    const remaining = new Set(extractPromptPlaceholders(renderedPrompt));
    return extractPromptPlaceholders(template).filter((name) => remaining.has(name));
}
// ---------------------------------------------------------------------------
// Shared identifiers
// ---------------------------------------------------------------------------
/** `ai_call_log.id` on the wire: a decimal string (BigInt in the DB). */
export const promptRunIdSchema = z
    .union([z.string(), z.number().int().nonnegative()])
    .transform((v) => String(v))
    .refine((v) => /^\d+$/.test(v), { message: 'runId must be a positive integer id' });
/** Where the row came from — mirrors the DB CHECK `ai_call_log_origin_chk`. */
export const promptRunOriginSchema = z.enum(['production', 'test']);
// ---------------------------------------------------------------------------
// GET /admin/prompts/:promptKey/runs — list (T04)
// ---------------------------------------------------------------------------
/** How many runs one page returns by default (the ring buffer keeps 20/origin). */
export const PROMPT_RUNS_DEFAULT_LIMIT = 50;
/** Hard ceiling — the list is a picker, not an export. */
export const PROMPT_RUNS_MAX_LIMIT = 100;
/**
 * Query of the list endpoint. Both parameters are optional; `origin` narrows the
 * journal to production material or to the admin's own test replays (the window
 * offers that filter because a tuning burst otherwise buries the production runs
 * the admin came to replay).
 *
 * Not `.strict()`: query strings pick up unrelated keys (cache-busters, tracking)
 * and rejecting a read for one would be hostile. Unknown keys are dropped.
 */
export const adminPromptRunsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(PROMPT_RUNS_MAX_LIMIT)
        .default(PROMPT_RUNS_DEFAULT_LIMIT),
    // An empty `?origin=` is "no filter", not a validation error.
    origin: z.preprocess((v) => (v === '' || v === null ? undefined : v), promptRunOriginSchema.optional()),
});
/**
 * ONE ROW OF THE LIST — deliberately body-less.
 *
 * `renderedPrompt` / `responseText` / `vars` are the bulk of a record (up to the
 * 256 KB record budget each row is allowed) and the picker needs none of them:
 * it shows date, origin, entity, model and outcome. The sizes are carried as
 * CHARACTER COUNTS computed in SQL (`length(...)`), so "how big is this run" is
 * answerable without the texts ever leaving the database.
 *
 * `provider` is a plain string, not an enum: this is a READ of historical rows,
 * and a run recorded by an older/other code path must never turn the whole list
 * into a 500 because its vendor label is not in today's union.
 */
export const promptRunSummarySchema = z.object({
    /** `ai_call_log.id` — BigInt server-side, a decimal string on the wire. */
    id: z.string(),
    promptKey: z.string(),
    origin: z.string(),
    /** ISO 8601. */
    createdAt: z.string(),
    /** `project:170`, `research_company:42`, … — what the call was run on. */
    entityRef: z.string().nullable(),
    provider: z.string(),
    /** The model that actually went into the request (not the configured pick). */
    model: z.string(),
    /** Tri-state, as everywhere in this feature (`null` ⇒ parsing did not apply). */
    parseOk: z.boolean().nullable(),
    /** The call itself failed — the text is in the detail's `error`. */
    hasError: z.boolean(),
    /** Size of the stored prompt / answer in characters (list-weight only). */
    promptChars: z.number().int().nonnegative(),
    responseChars: z.number().int().nonnegative().nullable(),
    /** For `origin='test'` — the production run this one replayed. */
    replayOfId: z.string().nullable(),
});
export const adminPromptRunsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    runs: z.array(promptRunSummarySchema),
});
// ---------------------------------------------------------------------------
// GET /admin/prompts/:promptKey/runs/:id — detail (T04)
// ---------------------------------------------------------------------------
/** `:promptKey` + `:id` path params of the detail endpoint. */
export const adminPromptRunParamsSchema = z.object({
    promptKey: z.string().min(1).max(100),
    id: promptRunIdSchema,
});
/**
 * One `{{variable}}` as it was recorded.
 *
 * `truncatedChars` is not cosmetic: values like `file_contents` (the extracted
 * text of the client's attachments) are cut at the storage threshold (T03), and
 * the window must SAY so — otherwise a prompt that looks starved of context
 * reads as a bad prompt rather than as a clipped log record.
 */
export const promptRunVarSchema = z.object({
    name: z.string(),
    /** The recorded value, exactly as stored (marker included when it was cut). */
    value: z.string(),
    /** Length of the ORIGINAL value in characters, before any truncation. */
    originalChars: z.number().int().nonnegative(),
    /** Characters dropped by the recorder, or `null` when nothing was cut. */
    truncatedChars: z.number().int().nonnegative().nullable(),
});
/**
 * THE FULL RECORD — the three things the spec's acceptance names: the
 * substitution data (`vars` + `defaults`), the text that was sent
 * (`renderedPrompt`) and the model's answer (`responseText`).
 *
 * It extends the summary rather than repeating it: the picker row and the opened
 * record are the same run, and a drift between the two shapes would show up as
 * the list and the detail disagreeing about a run's outcome.
 */
export const promptRunDetailSchema = promptRunSummarySchema.extend({
    /** sha256 of the template as it was at the moment of the run. */
    templateHash: z.string(),
    vars: z.array(promptRunVarSchema),
    /** Per-placeholder defaults this call was built with. */
    defaults: z.record(z.string()),
    /** What actually went into the model. */
    renderedPrompt: z.string(),
    /** What actually came back, before any parsing (`null` when the call failed). */
    responseText: z.string().nullable(),
    /** That answer run through the standard parser — for the comparison tab. */
    parsed: z.unknown(),
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    durationMs: z.number().int().nullable(),
    /** Error text when the call itself failed (then `responseText` is null). */
    error: z.string().nullable(),
    userId: z.string().nullable(),
});
export const adminPromptRunResponseSchema = z.object({
    success: z.literal(true).optional(),
    run: promptRunDetailSchema,
});
// ---------------------------------------------------------------------------
// POST /admin/prompts/:promptKey/test — request (T05)
// ---------------------------------------------------------------------------
/**
 * Body of the test call. `.strict()` — an unknown key here would most likely be
 * an attempt to steer the run (a forged `promptKey`, an `origin`), and the
 * prompt key comes from the path, the actor from the session.
 *
 *   runId    — which recorded run's `vars` + `defaults` to replay (required:
 *              there is no "invent the inputs" mode in v1, deliberately);
 *   template — the DRAFT from the window; omitted ⇒ the SAVED template is used;
 *   model    — override for this one call; omitted ⇒ the model the prompt row
 *              resolves to right now.
 */
export const adminPromptTestBodySchema = z
    .object({
    runId: promptRunIdSchema,
    template: z.string().optional(),
    model: z.string().min(1).max(100).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// POST /admin/prompts/:promptKey/test — result
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// The answer AS THE USER WOULD SEE IT (T04)
// ---------------------------------------------------------------------------
/**
 * How the live app draws one piece of a cleaned answer.
 *
 *   - `document` / `email` — rendered by the SAME `DocumentView` the project
 *     screens use; `docType` is the key it switches on;
 *   - `analysis`  — the pre-kickoff brief (summary / company / position / key
 *     points), drawn from the same view model the Pre-Kickoff panel uses;
 *   - `text`      — a plain-text answer as the live screen writes it (the salary
 *     figure that lands in the questionnaire answer field);
 *   - `structured`— this task has NO screen of its own: the cleaned, parsed
 *     result is shown as fields instead of an empty panel.
 */
export const promptTestPreviewViewSchema = z.enum([
    'document',
    'email',
    'analysis',
    'text',
    'structured',
]);
/**
 * ONE renderable piece of the cleaned answer. A single call can produce several
 * (`generate_documents` returns four documents; `pre_kickoff_analysis` returns
 * the brief plus three e-mail drafts), which is why this is a list and not one
 * blob: on the live screens those pieces are separate artifacts on separate
 * screens, and squashing them into one panel would not be "as the user sees it".
 */
export const promptTestPreviewItemSchema = z.object({
    view: promptTestPreviewViewSchema,
    /** `DocumentView` doc type; `''` when this item is not drawn by DocumentView. */
    docType: z.string(),
    /** The name the live screen gives this piece ('Job Description', 'Draft A'…). */
    label: z.string(),
    /**
     * The CLEANED content — the very object/string the production pipeline would
     * have saved: object for `document`/`email`/`analysis`/`structured`, string
     * for `text`.
     */
    content: z.unknown(),
});
/**
 * The test answer run through the SAME parse + cleanup the production pipeline
 * applies, ready to be drawn the way the live screens draw it (T04).
 *
 * It is built SERVER-SIDE on purpose. Every prompt has its own cleanup and they
 * are genuinely different — `generate_documents` uses `extractJsonLoose` and
 * appends the recruiter signature to each InMail, `generate_internal_notification`
 * uses `parseLlmJsonRobust` + a zod shape, `search_salary_rate` strips fences and
 * shapes `{found,salary,quote,note}` out of an answer whose `output_format` says
 * "plain text". Re-implementing that in the browser would make the tuning window
 * lie in exactly the situations it exists to diagnose, so the server calls the
 * production functions themselves and ships the result.
 */
export const promptTestPreviewSchema = z.object({
    /** Which production pipeline's parse + cleanup produced these items. */
    pipeline: z.string(),
    items: z.array(promptTestPreviewItemSchema),
    /**
     * What the live pipeline adds that a REPLAY cannot (project owner signature,
     * the appended sourcing sheet, the scraped source page…) or why the cleanup
     * produced nothing. Said out loud — an unexplained difference from production
     * is what makes a tuning tool untrustworthy.
     */
    note: z.string().nullable(),
});
/**
 * The outcome of ONE test call. Everything the window shows comes from here:
 * the string that was actually sent, the answer before any parsing, the parsed
 * answer, the drifted variables, and the journal row the run produced.
 */
export const promptTestResultSchema = z.object({
    /** What was actually sent: the draft with the replayed values substituted. */
    renderedPrompt: z.string(),
    /** The model's answer BEFORE parsing; `null` when the call failed. */
    rawText: z.string().nullable(),
    /** The parsed answer; `null` when parsing failed or the call failed. */
    parsed: z.unknown(),
    /**
     * The same answer after the PRODUCTION parse + cleanup of this prompt, split
     * into the pieces the live screens draw (T04). `null` when there is nothing to
     * draw at all — the call failed or the model answered with nothing; the window
     * then names that state instead (see `describeAnswer`).
     */
    preview: promptTestPreviewSchema.nullable(),
    /** `true`/`false` for JSON prompts, `null` when parsing does not apply. */
    parseOk: z.boolean().nullable(),
    /** Why the parse failed — shown instead of an empty result panel. */
    parseError: z.string().nullable(),
    /** Which provider the single call went to. */
    provider: z.enum(['anthropic', 'openai']),
    /** The model id that actually went into the request. */
    model: z.string(),
    /** Token counts when the provider reports them (it does not today → null). */
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    durationMs: z.number().int().nonnegative(),
    /** Placeholders in the draft with no value in the replayed run. */
    missingVars: z.array(z.string()),
    /** Set when the call itself failed (429 / 529 / timeout / no answer). */
    error: z.string().nullable(),
    /** The `origin='test'` journal row this call wrote; `null` if it could not. */
    runId: z.string().nullable(),
    /** The run that was replayed (`ai_call_log.id` as a string). */
    replayOfId: z.string(),
});
/** Wire body of the endpoint — flat, with the sibling routes' `success` flag. */
export const adminPromptTestResponseSchema = promptTestResultSchema.extend({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
//# sourceMappingURL=admin-prompt-runs.js.map